"use client"

import type { LogoRecord } from "@/lib/logos"

import { useCallback, useEffect, useState } from "react"
import { useRouter } from "next/router"
import Link from "next/link"
import Image from "next/image"
import { ImageOff, Lock, Plus, RotateCw, Shapes } from "lucide-react"

import { Seo } from "@/components/generals/seo"
import { Container } from "@/components/generals/container"
import { EmptyState } from "@/components/generals/empty-state"
import { Section } from "@/components/generals/section"
import { useAuth } from "@/components/auth-provider"
import { Button } from "@/components/ui/button"

function LogoGridSkeleton() {
  return (
    <div
      aria-hidden
      className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6"
    >
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex flex-col gap-3">
          <div className="aspect-square animate-pulse rounded-lg bg-surface-2" />
          <div className="flex flex-col gap-2">
            <div className="h-3 w-3/4 animate-pulse rounded-sm bg-surface-2" />
            <div className="h-2.5 w-1/2 animate-pulse rounded-sm bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function MyLogosPage() {
  const router = useRouter()
  const { user, loading } = useAuth()
  const [logos, setLogos] = useState<LogoRecord[]>([])
  const [fetching, setFetching] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!loading && !user) {
      router.replace(`/login?returnTo=${encodeURIComponent("/mylogo")}`)
    }
  }, [loading, user, router])

  const loadLogos = useCallback(async () => {
    setFetching(true)
    setFailed(false)
    try {
      const res = await fetch("/api/logos", { cache: "no-store" })
      if (!res.ok) {
        setFailed(true)
        return
      }
      const data = await res.json()
      setLogos(data.logos || [])
    } catch {
      setFailed(true)
    } finally {
      setFetching(false)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    void loadLogos()
  }, [user, loadLogos])

  const pending = loading || !user || fetching

  return (
    <>
      <Seo
        title="My logos"
        description="Every logo you have generated, ready to open and download."
        noIndex
      />

      <Section id="my-logos" size="md">
        <Container width="wide">
          <div className="flex flex-col gap-6 border-b border-line pb-8 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-3">
              <h1 className="font-display text-h2 text-balance">My logos</h1>
              <p className="text-body text-ink-muted text-pretty">
                Everything you have generated. Open one to unlock and download its files.
              </p>
            </div>

            <Button asChild className="shrink-0">
              <Link href="/">
                <Plus aria-hidden /> New logo
              </Link>
            </Button>
          </div>

          <div className="mt-10">
            {pending ? (
              <LogoGridSkeleton />
            ) : failed ? (
              <EmptyState
                tone="danger"
                icon={ImageOff}
                title="We could not load your logos"
                description="The request did not come back. Your logos are safe. Try again, or start a new one in the meantime."
                action={
                  <Button onClick={() => void loadLogos()}>
                    <RotateCw aria-hidden /> Try again
                  </Button>
                }
                secondaryAction={
                  <Button asChild variant="outline">
                    <Link href="/">Make a new logo</Link>
                  </Button>
                }
              />
            ) : logos.length === 0 ? (
              <EmptyState
                icon={Shapes}
                title="No logos here yet"
                description="Describe your brand in a sentence. For $7 you get three concepts and every file in about a minute."
                action={
                  <Button asChild>
                    <Link href="/">Create your first logo</Link>
                  </Button>
                }
                secondaryAction={
                  <Button asChild variant="outline">
                    <Link href="/community">Browse the gallery</Link>
                  </Button>
                }
              />
            ) : (
              <ul className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
                {logos.map((logo) => (
                  <li key={logo.id}>
                    <Link href={`/logo/${logo.id}`} className="group block">
                      <div className="relative aspect-square overflow-hidden rounded-lg border border-line bg-artboard transition-colors duration-fast ease-out group-hover:border-line-strong">
                        {logo.imageUrl && (
                          <Image
                            src={logo.imageUrl}
                            alt={logo.brandName || "Untitled logo"}
                            fill
                            className="object-contain p-4"
                            unoptimized
                          />
                        )}
                        {logo.isPrivate && (
                          <span
                            className="absolute right-2 top-2 rounded-full border border-line bg-surface p-1.5 text-ink-muted"
                            title="Private logo"
                          >
                            <Lock className="size-3.5" aria-hidden />
                            <span className="sr-only">Private logo</span>
                          </span>
                        )}
                      </div>
                      <div className="pt-3">
                        <p className="truncate text-small font-medium text-ink decoration-line-strong underline-offset-4 group-hover:underline">
                          {logo.brandName || "Untitled"}
                        </p>
                        <p className="text-caption text-ink-subtle">
                          {new Date(logo.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Container>
      </Section>
    </>
  )
}
